// When the ordering page may seed its cart from orders/current (customer-entry.tsx).
//
// The cart is seeded ONCE, and a submit REPLACES the order on its day with the cart, so
// the seed must come from the server as it is now. Only a read that SUCCEEDED after this
// page mounted qualifies:
//  - still fetching → the list on hand may predate the last submit (Gomes, 2026-10-25/26:
//    Back from the summary seeded the cart with the pre-submit order);
//  - the refetch failed → TanStack keeps the cached, possibly stale, list next to the error;
//  - data older than the mount → nothing fetched it for this page yet.
// Otherwise the page shows a retry state instead of a cart.
export interface OrdersReadState {
  isFetching: boolean;
  isError: boolean;
  /** TanStack's dataUpdatedAt: ms of the last successful write of the data, 0 if none. */
  dataUpdatedAt: number;
}

export function ordersReadyToSeed(read: OrdersReadState, pageMountedAt: number): boolean {
  return !read.isFetching && !read.isError && read.dataUpdatedAt >= pageMountedAt;
}

/** The read settled without a usable list: show "couldn't load your orders" + retry. */
export function ordersReadFailed(read: OrdersReadState, pageMountedAt: number): boolean {
  return !read.isFetching && read.isError && read.dataUpdatedAt < pageMountedAt;
}

// The options orders/current must carry on the ordering page. refetchOnMount alone is
// not enough: the page mounts before it knows the account, so the query starts disabled
// and only later switches to the account's key. refetchOnMount runs only when an
// observer first subscribes; a key change fetches only a STALE query, and under the
// app's staleTime Infinity a list cached by the last submit is never stale — no fetch,
// no seed (caught by buyer-journey on #375, a35ee36b). staleTime 0 makes the switch fetch.
export const ORDERS_CURRENT_OPTIONS = { refetchOnMount: "always", staleTime: 0 } as const;
